import { get } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useLazyQuery, useQuery } from "shared/hooks/useApi";
import { OFFERS } from "utils/api/graphql/queries/offers";
import Card from "./components/Card";
import DetailsDrawer from "./components/DetailsDrawer";
import ProductsDrawer from "./components/ProductsDrawer";
import useInsurancePlans from "./hooks/useInsurancePlans";

const MAX_SELECTED = 2;

const List = ({
  selectedModules,
  setSelectedModules,
  cost,
  currency,
  insurancePlan,
  disabled,
  translation,
  project,
}) => {
  const { id: projectId } = useParams();
  const [showMore, setShowMore] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [openDetails, setOpenDetails] = useState(null);

  const [getOffers, { loading: offersLoading }] = useLazyQuery(OFFERS);
  const {
    selectedInsurancePlans,
    selectedInsuranceOptions,
    setSelectedInsuranceOptions,
    fetchMainOffers,
    fetchOptions,
  } = useInsurancePlans({
    insurancePlan,
    getOffers,
    project,
    setSelectedModules,
    setShowOptions,
  });

  useQuery(OFFERS, {
    variables: {
      where: {
        AND: [
          { offerInsurancePlans: { insurancePlan: { id: insurancePlan.id } } },
          { offerInsurancePlans: { main: true } },
        ],
      },
    },
    onCompleted: fetchMainOffers,
  });

  const sortModules = useCallback((a, b) => {
    const weightDiff = Number(b.weight) - Number(a.weight);
    if (weightDiff !== 0) return weightDiff;
    const nameA = a.insurancePlan?.name?.toLowerCase() || "";
    const nameB = b.insurancePlan?.name?.toLowerCase() || "";
    return nameA.localeCompare(nameB);
  }, []);

  const handleModuleCheck = useCallback(
    ({ module, checked }) => {
      const productId = get(module, "insurancePlan.id");

      if (checked) {
        fetchOptions(productId);
      } else {
        setSelectedInsuranceOptions((prev) => ({
          ...prev,
          [productId]: [],
        }));
      }

      setSelectedModules((prev) => {
        const existingList = get(prev, insurancePlan.id, []);
        const moduleExist = existingList.find(
          ({ insurancePlan }) => insurancePlan.id === productId,
        );

        const isMaxReached =
          get(selectedModules, insurancePlan.id, []).filter(
            ({ weight }) => weight === 1 && weight === module?.weight,
          ).length >= MAX_SELECTED;

        if (!moduleExist && isMaxReached) return prev;

        return moduleExist
          ? {
              ...prev,
              [insurancePlan.id]: existingList.filter(
                ({ insurancePlan }) =>
                  insurancePlan.id !== productId &&
                  !get(selectedInsuranceOptions, productId, [])
                    .map(({ insurancePlan }) => insurancePlan.id)
                    .includes(insurancePlan.id),
              ),
            }
          : {
              ...prev,
              [insurancePlan.id]: [...existingList, module].sort(sortModules),
            };
      });
    },
    [
      fetchOptions,
      insurancePlan.id,
      selectedInsuranceOptions,
      selectedModules,
      setSelectedInsuranceOptions,
      setSelectedModules,
      sortModules,
    ],
  );

  const productsDrawerProps = useMemo(
    () => ({
      onOpenDetails: setOpenDetails,
      selectedInsurancePlans,
      selectedModules,
      selectedInsuranceOptions,
      onCheck: handleModuleCheck,
      insurancePlan,
      cost,
      currency,
      disabled,
      translation,
    }),
    [
      selectedInsurancePlans,
      selectedModules,
      selectedInsuranceOptions,
      handleModuleCheck,
      insurancePlan,
      cost,
      currency,
      disabled,
      translation,
    ],
  );

  return (
    <>
      <Card
        onCheck={handleModuleCheck}
        selectedModules={selectedModules}
        products={selectedInsurancePlans}
        onOpenDetails={setOpenDetails}
        onShowMore={setShowMore}
        mainProduct={insurancePlan.id}
        translation={translation}
        loading={offersLoading}
        disabled={disabled}
      />

      {openDetails && (
        <DetailsDrawer
          open={openDetails}
          onClose={() => setOpenDetails(null)}
          filter={{
            where: {
              insurancePlan: { id: openDetails },
              project: { id: projectId },
            },
          }}
          translation={translation}
        />
      )}

      {(showOptions || showMore) && (
        <ProductsDrawer
          open={showMore || showOptions}
          onClose={() => {
            setShowMore(false);
            setShowOptions(false);
          }}
          {...productsDrawerProps}
        />
      )}
    </>
  );
};

export default List;
